import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, KeyRound } from "lucide-react";
import api, { formatErr } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { BRAND_COLORS } from "../../lib/bookingStatus";
import CustomerPinPromptDialog from "./CustomerPinPromptDialog";
import { deDateMediumTime } from "../../lib/dateFormat";

/*
 Feb 2026 · User-Directive „Customer-PIN".
 Sichtbare Karte im /app/settings-Bereich: zeigt den aktuellen Status
 (PIN gesetzt seit / nicht gesetzt) und stellt drei Aktionen bereit:
  - PIN einrichten (nur wenn noch keine existiert)
  - PIN ändern (aktuelle PIN + neue PIN)
  - PIN vergessen — sendet OTP an die eigene E-Mail und wechselt in den
    Reset-Modus (Reset-Code + neue PIN).
*/
export default function CustomerPinSection() {
  const { lang } = useI18n();
  const de = lang === "de";
  const [status, setStatus] = useState({ is_set: false, updated_at: null });
  const [loaded, setLoaded] = useState(false);
  const [mode, setMode] = useState(null);   // "setup" | "change" | "reset"
  const [requestingReset, setRequestingReset] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/me/customer-pin/status");
      setStatus({ is_set: !!data.is_set, updated_at: data.updated_at || null });
    } catch (e) { /* customers only endpoint; silent */ }
    finally { setLoaded(true); }
  };

  useEffect(() => { load(); }, []);

  const startForgot = async () => {
    setRequestingReset(true);
    try {
      await api.post("/me/customer-pin/request-reset");
      toast.success(de
        ? "Reset-Code an Ihre E-Mail gesendet."
        : "Reset code sent to your email.");
      setMode("reset");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally { setRequestingReset(false); }
  };

  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      return deDateMediumTime(iso);
    } catch { return iso; }
  };

  return (
    <section
      className="bg-white border border-navy/10 p-6 md:p-8"
      data-testid="customer-pin-section"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2.5 mb-2">
            <ShieldCheck className="w-5 h-5" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.6} />
            <div className="label-caps">{de ? "Digitale Signatur" : "Digital signature"}</div>
          </div>
          <h2 className="font-display text-2xl text-navy" data-testid="customer-pin-title">
            {de ? "Ihre persönliche PIN" : "Your personal PIN"}
          </h2>
          <p className="text-[13px] text-navy-dark/70 mt-2 max-w-xl leading-relaxed">
            {de
              ? "Ihre 4-stellige PIN dient als digitale Signatur, wenn Sie ein Angebot annehmen oder ablehnen. Ohne PIN können Sie Angebots-Entscheidungen nicht bestätigen."
              : "Your 4-digit PIN acts as a digital signature when you approve or decline a quote. Without a PIN you cannot confirm quote decisions."}
          </p>
        </div>
        <div
          className="border px-3 py-1.5 text-[10.5px] tracking-[0.18em] uppercase"
          style={{
            borderColor: status.is_set ? `${BRAND_COLORS.greenDeep}55` : `${BRAND_COLORS.gold}55`,
            color: status.is_set ? BRAND_COLORS.greenDeep : BRAND_COLORS.gold,
          }}
          data-testid={`customer-pin-status-${status.is_set ? "set" : "unset"}`}
        >
          {status.is_set
            ? (de ? "Aktiv" : "Active")
            : (de ? "Noch nicht eingerichtet" : "Not set")}
        </div>
      </div>

      {loaded && status.is_set && status.updated_at && (
        <p className="text-[12px] text-navy-dark/55 mt-3 italic">
          {de ? "Zuletzt aktualisiert" : "Last updated"}: {fmtDate(status.updated_at)}
        </p>
      )}

      <div className="mt-6 flex flex-wrap gap-3">
        {!status.is_set && (
          <button
            type="button"
            data-testid="customer-pin-setup-btn"
            onClick={() => setMode("setup")}
            className="inline-flex items-center gap-2 text-ivory px-5 py-3 text-[11px] tracking-[0.18em] uppercase hover:opacity-90 transition-opacity"
            style={{ background: BRAND_COLORS.navy }}
          >
            <KeyRound className="w-4 h-4" strokeWidth={1.6} />
            {de ? "PIN einrichten" : "Set up PIN"}
          </button>
        )}
        {status.is_set && (
          <>
            <button
              type="button"
              data-testid="customer-pin-change-btn"
              onClick={() => setMode("change")}
              className="inline-flex items-center gap-2 text-ivory px-5 py-3 text-[11px] tracking-[0.18em] uppercase hover:opacity-90 transition-opacity"
              style={{ background: BRAND_COLORS.navy }}
            >
              <KeyRound className="w-4 h-4" strokeWidth={1.6} />
              {de ? "PIN ändern" : "Change PIN"}
            </button>
            <button
              type="button"
              data-testid="customer-pin-forgot-btn"
              onClick={startForgot}
              disabled={requestingReset}
              className="inline-flex items-center gap-2 border border-navy/25 text-navy px-5 py-3 text-[11px] tracking-[0.18em] uppercase hover:bg-navy/5 transition-colors disabled:opacity-50"
            >
              {requestingReset
                ? (de ? "Code wird gesendet…" : "Sending code…")
                : (de ? "PIN vergessen" : "Forgot PIN")}
            </button>
          </>
        )}
      </div>

      <CustomerPinPromptDialog
        open={!!mode}
        mode={mode || "setup"}
        de={de}
        onClose={() => setMode(null)}
        onSuccess={() => {
          setMode(null);
          load();
        }}
      />
    </section>
  );
}
